// Globals
import { ChangeEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';

// Modules
import { selectChannel } from '@/modules/Channels';
import { Header } from '@/modules/Layout';

// Models
import { IBrand } from '@/models/brands.model';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { getBrands, uploadBrandDetails } from '../../feature';

// Components
import { showSuccessToast } from '@/components';
import { Pagination, Table } from '@/ui';
import { StyledContainer } from './BrandsContainerStyles';
import { EditModal } from '../../components';

// Helpers
import { GetBrandsDTO, UpdateBrandsDTO } from '../../dtos';
import { getBrandsTableConfig, TUpdateBrandFormValues } from '../../helpers';
import { useGetBrands } from '../../hooks';

const BrandsContainer = () => {
  const { t } = useTranslation();
  const [selectedBrand, setSelectedBrand] = useState<IBrand | null>(null);

  const dispatch = useAppDispatch();
  const channel = useAppState(selectChannel);
  const { brands, isLoading, pagination } = useGetBrands();

  const handlePageChange = (page: number) => {
    if (!channel) return;
    const dto = new GetBrandsDTO({ page, channelId: channel.id });
    dispatch(getBrands(dto));
  };

  const handleClose = () => setSelectedBrand(null);

  const handleUpdateBrand = useCallback(
    async (values: TUpdateBrandFormValues) => {
      if (!selectedBrand || !channel) return;
      const updateDto = new UpdateBrandsDTO({
        ...values,
        id: selectedBrand?.id,
        channelId: channel.id,
      });
      try {
        await dispatch(uploadBrandDetails(updateDto)).unwrap();

        setSelectedBrand(null);

        showSuccessToast({
          message: t('brand_image_updated'),
        });
      } catch {}
    },
    [selectedBrand, dispatch, channel, t],
  );

  const handleSearch = debounce((event: ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value.trim();
    if (!channel) return;
    const dto = new GetBrandsDTO({ page: 1, channelId: channel.id, search: query || undefined });
    dispatch(getBrands(dto));
  }, 500);

  const tableConfig = getBrandsTableConfig(t, handleSearch);

  return (
    <>
      <Header title={t('menu_brands')} />
      <StyledContainer>
        <Table
          loading={isLoading}
          dataSource={brands ?? []}
          rowKey="id"
          onRow={(record) => ({
            onClick: () => setSelectedBrand(record),
          })}
          columns={tableConfig}
          pagination={false}
          scroll={{ x: 'max-content' }}
        />
        <Pagination
          hideOnSinglePage
          total={pagination?.total}
          current={pagination?.page}
          pageSize={pagination?.pageSize}
          align="center"
          onChange={handlePageChange}
        />
        <EditModal
          visible={!!selectedBrand}
          handleClose={handleClose}
          onSubmit={handleUpdateBrand}
          initialValues={selectedBrand as IBrand}
        />
      </StyledContainer>
    </>
  );
};

export default BrandsContainer;
